import { baseApi } from './baseApi';

/**
 * Zendesk Sell Deals API endpoints
 * Handles fetching deals and stages from Zendesk Sell via our backend proxy
 */
export const dealsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /zendesk-deals - Fetch deals with pagination, search, filtering, and sorting
    getDeals: builder.query({
      query: ({ page = 1, limit = 10, search, hot, stage_id, pipeline_id, owner, show_inactive, sort_by, sort_direction } = {}) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (search) params.append('search', search);
        if (hot !== undefined && hot !== null && hot !== '') params.append('hot', hot);
        if (stage_id) params.append('stage_id', stage_id);
        if (pipeline_id) params.append('pipeline_id', pipeline_id);
        if (owner) params.append('owner', owner);
        if (show_inactive !== undefined) params.append('show_inactive', show_inactive);
        if (sort_by) params.append('sort_by', sort_by);
        if (sort_direction) params.append('sort_direction', sort_direction);
        return `/zendesk-deals?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.deals
          ? [
              ...result.deals.map(({ id }) => ({ type: 'Deal', id })),
              { type: 'Deal', id: 'LIST' },
            ]
          : [{ type: 'Deal', id: 'LIST' }],
      // Transform the response to ensure consistent data structure
      transformResponse: (response) => {
        const deals = response?.deals || [];
        return {
          deals: deals.map((deal) => ({
            ...deal,
            // Normalize value to always be a number for easier sorting
            value_numeric: typeof deal.value === 'string' 
              ? parseFloat(deal.value) 
              : (deal.value || 0),
          })),
          pagination: response?.pagination || {
            current_page: 1,
            per_page: deals.length,
            total: deals.length,
            total_pages: 1
          },
          meta: response?.meta || { total_count: deals.length },
        };
      },
    }),

    // GET /zendesk-stages - Fetch all pipeline stages from Zendesk Sell
    getStages: builder.query({
      query: () => '/zendesk-stages',
      // Cache stages for longer since they rarely change
      keepUnusedDataFor: 3600, // 1 hour
      transformResponse: (response) => {
        const stages = response?.stages || [];
        return {
          stages: stages.map((stage) => ({
            ...stage,
            // Determine if this is a "won" stage based on category or position
            isWonStage: stage.category === 'won',
          })),
          meta: response?.meta || { count: stages.length },
        };
      },
    }),

    // POST /zendesk-deals/sync - Sync deals from Zendesk Sell API
    syncDeals: builder.mutation({
      query: () => ({
        url: '/zendesk-deals/sync',
        method: 'POST',
      }),
      // Invalidate deals list after successful sync
      invalidatesTags: [{ type: 'Deal', id: 'LIST' }],
    }),

    // GET /zendesk-deals/:id/tasks - Fetch tasks linked to a specific deal
    getDealTasks: builder.query({
      query: (dealId) => `/zendesk-deals/${dealId}/tasks`,
      providesTags: (result, error, dealId) => [
        { type: 'Deal', id: dealId },
        { type: 'DealTasks', id: dealId },
      ],
      transformResponse: (response) => {
        const tasks = response?.tasks || [];
        return {
          tasks: tasks.map((task) => ({
            ...task,
            // Determine status based on done_at
            status: task.done_at ? 'completed' : 'pending',
          })),
          meta: response?.meta || { total_count: tasks.length },
        };
      },
    }),

    // GET /daily-report - Fetch daily report statistics
    getDailyReport: builder.query({
      query: () => '/daily-report',
      providesTags: ['DailyReport'],
      transformResponse: (response) => {
        return {
          reportDate: response?.report_date,
          generatedAt: response?.generated_at,
          deals: response?.deals || {},
          tasks: response?.tasks || {},
        };
      },
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetDealsQuery,
  useGetStagesQuery,
  useSyncDealsMutation,
  useGetDealTasksQuery,
  useGetDailyReportQuery,
} = dealsApi;

